import type { User } from 'oidc-client-ts';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { currentLocale } from '../i18n/index.ts';
import { AuthContext, type AuthStatus, EXPIRY_MARGIN_SECONDS, renew } from './useAuth.ts';
import { type SignInState, userManager } from './userManager.ts';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  // signoutRedirect() removes the stored user *before* navigating away; without this flag the
  // resulting "anonymous" state makes RequireAuth start a sign-in that races the logout and,
  // with the Keycloak SSO session still alive, silently signs the user straight back in.
  const signingOut = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const apply = (next: User | null) => {
      if (cancelled) return;
      setUser(next);
      if (signingOut.current) return;
      setStatus(next && !next.expired ? 'authenticated' : 'anonymous');
    };

    void (async () => {
      const stored = await userManager.getUser();
      if (stored?.expired && stored.refresh_token) apply(await renew());
      else apply(stored);
    })();

    const onLoaded = (loaded: User) => {
      apply(loaded);
    };
    const onUnloaded = () => {
      apply(null);
    };
    const onRenewError = () => {
      void userManager.removeUser();
    };
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    userManager.events.addSilentRenewError(onRenewError);
    return () => {
      cancelled = true;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
      userManager.events.removeSilentRenewError(onRenewError);
    };
  }, []);

  const signIn = useCallback(async (returnTo = '/harbour') => {
    await userManager.signinRedirect({
      state: { returnTo } satisfies SignInState,
      ui_locales: currentLocale(),
    });
  }, []);

  const signUp = useCallback(async (returnTo = '/harbour') => {
    await userManager.signinRedirect({
      state: { returnTo } satisfies SignInState,
      ui_locales: currentLocale(),
      // OIDC "prompt=create": Keycloak opens the registration form instead of the login form.
      prompt: 'create',
    });
  }, []);

  const signOut = useCallback(async () => {
    signingOut.current = true;
    setStatus('signing-out');
    try {
      await userManager.signoutRedirect({ extraQueryParams: { ui_locales: currentLocale() } });
    } catch (error) {
      // Keycloak unreachable: drop the local session anyway so the user is signed out here.
      signingOut.current = false;
      await userManager.removeUser();
      setStatus('anonymous');
      console.error('Sign-out redirect failed', error);
    }
  }, []);

  const getAccessToken = useCallback(async () => {
    let current = await userManager.getUser();
    if (!current) return null;
    if (current.expires_in !== undefined && current.expires_in < EXPIRY_MARGIN_SECONDS) {
      current = await renew();
    }
    return current?.access_token ?? null;
  }, []);

  const value = useMemo(
    () => ({ status, user, signIn, signUp, signOut, getAccessToken }),
    [status, user, signIn, signUp, signOut, getAccessToken],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}
